import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ShoppingCart } from "lucide-react";

export const useIsMobile = (bp = 767) => {
  const [m, setM] = useState(() => window.matchMedia(`(max-width: ${bp}px)`).matches);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    const fn = (e) => setM(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, [bp]);
  return m;
};

// Tappable headline row shown ONLY on mobile; the section it controls is hidden via [data-fold="closed"].
export const MobileFoldRow = ({ id, icon: Icon, title, teaser, open, onToggle, accent = "#C41E24" }) => (
  <button
    type="button"
    data-testid={`fold-${id}`}
    aria-expanded={open}
    onClick={() => {
      onToggle(id);
      if (open) setTimeout(() => document.querySelector(`[data-testid="fold-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
    }}
    className={`md:hidden w-full text-left flex items-center gap-4 px-6 py-5 border-b border-white/10 transition-colors ${open ? "bg-[#141414]" : "bg-[#0d0d0d] active:bg-[#161616]"}`}
  >
    <span className="shrink-0 w-10 h-10 rounded-md flex items-center justify-center border" style={{ borderColor: `${accent}66`, background: `${accent}14`, color: accent }}>
      <Icon size={18} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block font-headings text-xl uppercase tracking-wide leading-none">{title}</span>
      <span className="block text-xs text-white/50 mt-1 truncate">{teaser}</span>
    </span>
    <ChevronDown size={18} className={`shrink-0 text-white/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
  </button>
);

// Sticky bottom conversion bar — appears once the hero has scrolled out of view.
export const MobileStickyCta = ({ label, onClick, disabled, watch = "[data-testid='hero-section']" }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = document.querySelector(watch);
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setShow(!e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, [watch]);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="mobile-sticky-cta"
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }} transition={{ duration: 0.3, ease: "easeOut" }}
          className="md:hidden fixed inset-x-0 bottom-0 z-40 px-3 pt-2 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/95 to-transparent"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 0.6rem)" }}
        >
          <button
            data-testid="mobile-sticky-cta-btn"
            onClick={onClick}
            disabled={disabled}
            className="cta-glow w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white font-subheadings text-sm font-bold uppercase tracking-wider py-3.5 rounded-md shadow-[0_10px_30px_rgba(196,30,36,0.45)] disabled:opacity-70"
          >
            <ShoppingCart size={16} /> {label}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
